import * as z from "zod";
import { CardSummary } from "./card";
import { isoDate } from "./common";
import { FeeRates } from "./fees";

/**
 * `EXPIRED` is a real state, not a theoretical one: a cart is held for the
 * store's whole `quoteValidityMinutes` and the sweep closes it afterwards. It
 * matches `enum CustomerCartStatus` in the schema.
 */
export const CUSTOMER_CART_STATUSES = ["OPEN", "PAID", "EXPIRED"] as const;
export type CustomerCartStatus = (typeof CUSTOMER_CART_STATUSES)[number];

export const CHECKOUT_STATUSES = ["PENDING", "PAID", "EXPIRED", "CANCELED"] as const;
export type CheckoutStatus = (typeof CHECKOUT_STATUSES)[number];

export const OFFER_CHANNELS = ["STANDALONE", "SHOPIFY", "EBAY", "CUSTOMER"] as const;
export const OFFER_STATUSES = ["ACTIVE", "SOLD", "EXPIRED", "ARCHIVED"] as const;

/** A price the shop put on a card, on one channel. A CUSTOMER offer is the quote behind a cart item. */
export const CardOffer = z.object({
  id: z.string(),
  cardId: z.string(),
  storeId: z.string(),
  channel: z.enum(OFFER_CHANNELS),
  status: z.enum(OFFER_STATUSES),
  priceCents: z.number(),
  currencyCode: z.string(),
  externalProductId: z.string().nullable(),
  externalVariantId: z.string().nullable(),
  customerId: z.string().nullable(),
  expiresAt: isoDate.nullable(),
  stockId: z.string().nullable(),
  card: CardSummary,
  createdAt: isoDate,
  updatedAt: isoDate,
});
export type CardOffer = z.output<typeof CardOffer>;

/** One quoted card in a cart. Named by its offer on the wire, although the OpenAPI class says `scanId`. */
export const CustomerCartItem = z.object({
  id: z.string(),
  offerId: z.string(),
  /** The scan the line came from; the offer id on lines that predate it. */
  scanId: z.string().nullable().optional(),
  includedInPurchaseOrder: z.boolean(),
  quantity: z.number(),
  metadata: z.unknown().nullable().optional(),
  offer: CardOffer,
});
export type CustomerCartItem = z.output<typeof CustomerCartItem>;

/** A payment attempt against a customer cart. */
export const Checkout = z.object({
  id: z.string(),
  customerCartId: z.string(),
  /** `shopify` or `stripe`. */
  providerKey: z.string(),
  status: z.enum(CHECKOUT_STATUSES),
  /** Where the buyer completes payment. */
  url: z.string().nullable(),
  amountTotal: z.number().nullable(),
  subtotalAmount: z.number().nullable(),
  taxAmount: z.number().nullable(),
  /** HobbyScan technology fee the customer paid; null where none applied (Shopify, or a rate of 0). */
  platformFeeAmount: z.number().nullable(),
  /** Card processing fee the customer paid, kept apart from the technology fee; null where none applied. */
  processingFeeAmount: z.number().nullable(),
  currency: z.string().nullable(),
  expiresAt: isoDate.nullable(),
  createdAt: isoDate,
});
export type Checkout = z.output<typeof Checkout>;

export const CustomerCart = z.object({
  id: z.string(),
  status: z.enum(CUSTOMER_CART_STATUSES),
  storeId: z.string(),
  /** The counter the cart belongs to; null when it could not be told. */
  locationId: z.string().nullable(),
  storeDomain: z.string().nullable(),
  salesTaxAtCheckout: z.boolean(),
  /** The fees the payment page will add; null when the shop's checkout is Shopify's. */
  checkoutFees: FeeRates.nullable(),
  checkoutUrl: z.string().nullable(),
  checkout: Checkout.nullable(),
  /** When the shop stops holding the cards; null once paid or expired. */
  expiresAt: isoDate.nullable(),
  createdAt: isoDate,
  updatedAt: isoDate,
  items: z.array(CustomerCartItem),
});
export type CustomerCart = z.output<typeof CustomerCart>;

export const CustomerCartUser = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  isGuest: z.boolean(),
  isPos: z.boolean(),
});

/** A cart as the shop sees it: the customer beside it. */
export const AdminCustomerCart = CustomerCart.extend({ user: CustomerCartUser });
export type AdminCustomerCart = z.output<typeof AdminCustomerCart>;

export const CustomerCartCreated = z.object({
  id: z.string(),
  status: z.enum(CUSTOMER_CART_STATUSES),
});
export type CustomerCartCreated = z.output<typeof CustomerCartCreated>;

/** A typed-amount sale: a one-line cart for a new counter customer. */
export const QuickCharge = z.object({
  customerCartId: z.string(),
  customerId: z.string(),
});
export type QuickCharge = z.output<typeof QuickCharge>;

/** The cart-specific fields of one item. Both optional; the body must name at least one. */
export interface CustomerCartItemEdit {
  quantity?: number;
  /** Whether the item goes into the purchase; an excluded item stays listed. */
  included?: boolean;
}

export interface CheckoutOptions {
  /** Omit to let the store's connections decide; name one to force it. */
  provider?: "shopify" | "stripe";
  /** `mobile` returns the buyer to the in-app success page, `site` to the shop's scan page on hobbyshop.ai. */
  platform?: "mobile" | "site";
}
